import React from 'react';
import OrderDirectionsEnum from '../../../Enums/OrderDirectionsEnum';
import ShownPanelsEnum from '../../../Enums/ShownPanelsEnum';
import LeadSearchParams from './LeadSearchParams';
import ButtonRow from '../../../Elements/ButtonRow';
import LeadService from '../../../Services/leadService';
import LeadItem from '../../../Domain/LeadItem';
import ImportLeadsWindowDescription from './ImportLeadsWindowDescription';
import LeadImportParameters from './LeadImportParameters';
import ErrorMessage from '../../../Elements/ErrorMessage';
import CrudTypesEnum from '../../../Enums/CrudTypesEnum';
import CompanyService from '../../../Services/CompanyService';
import urlHelper from '../../../Helpers/urlHelper';
import ButtonColorsEnum from '../../../Enums/ButtonColorsEnum';
import GeneralEditWizzardBase from '../../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseFigma';
import EditLeadWindowDescription from './EditLeadWindowDescription';
import TabOfDataTable from '../../../Elements/DataTable/TabOfDataTable';

export default class LeadAdmin extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      leadList: [],
      selected: -1,
      selectedItem: null,
      orderBy: 'Id',
      orderDirection: OrderDirectionsEnum.Asc,
      pageNumber: 1,
      numberOfElementsOnAPage: 20,
      numberOfElementsInTheTable: 0,
      shownPanel: ShownPanelsEnum.None,
      // Deep-link seed for global record search (CD-5425), on the lead's own id.
      // It REPLACES the search item rather than extending it, and that is load-bearing:
      // LeadSearchParams defaults IsActive = 'true' AND UserId = the logged-in user, so the page
      // normally shows only active leads assigned to you. A link pointing at an inactive lead, or at
      // someone else's, would otherwise land on an empty grid and look broken while working perfectly.
      searchItem: new URLSearchParams(window.location.search).get('LeadId')
        ? { LeadId: new URLSearchParams(window.location.search).get('LeadId') }
        : new LeadSearchParams(),
    };
    this.handleButtonRowButtonClick = this.handleButtonRowButtonClick.bind(this);
    this.handleExportExcelButtonClick = this.handleExportExcelButtonClick.bind(this);
    LeadItem.editFunction = this.editFunction.bind(this);
    LeadItem.lockFunction = this.lockFunction.bind(this);
    LeadItem.convertFunction = this.convertFunction.bind(this);
    this.clickOnConvertButtonInPopup = this.clickOnConvertButtonInPopup.bind(this);
    this.refresh = this.refresh.bind(this);
  }

  editFunction(id, item, itemList) {
    this.setState({
      shownPanel: ShownPanelsEnum.Edit,
      selectedItem: item,
    });
  }

  lockFunction(id, item) {
    if (item.StatusCode === 5) {
      ErrorMessage.SetWarning('This lead is already converted and cannot be locked.');
      return;
    }
    ErrorMessage.SetApprove('Are you sure you want to lock this lead?', () => {
      const tempItem = { ...item, IsActive: !item.IsActive };
      const tempList = [...this.state.leadList];
      tempList[tempList.findIndex((l) => l.LeadId === id)] = tempItem;
      this.setState({ leadList: tempList });
      LeadService.EditLead({ ...tempItem })
        .then((result) => {
          this.refresh();
          ErrorMessage.SetSuccess('The lead has been modified successfully.');
        })
        .catch((error) => ErrorMessage.SetErrorMessage('LA76'));
    });
  }

  convertFunction(id, item, searchItem) {
    CompanyService.GetMultipleClientCompanyMatches(item.CompanyName)
      .then((result) => {
        if (result.exists) {
          this.setPopupState(id, `There are companies with a similar name: ${result.company}`, item.CompanyName);
        } else {
          this.setPopupState(id, '', item.CompanyName);
        }
      })
      .catch((err) => {
        ErrorMessage.SetErrorMessage('LA93');
        this.setPopupState(id, '', item.CompanyName);
      });
  }

  renderRedirectLinkTag(companyName) {
    const url = urlHelper.generateRedirectUrlWithMultipleParameters('sales_clients', [
      { key: 'SimilarName', value: companyName },
      { key: 'OfficeId', value: '' },
    ]);
    return (
      <div className={'text-left p-1 text-danger'}>
        <a
          href={url}
          target={'_blank'}
        >
          <i className='fa-solid fa-magnifying-glass-arrow-right' /> Open new tab to display matching clients
        </a>
      </div>
    );
  }

  setPopupState(id, existingCompanyWarning, companyName = '') {
    const message = (
      <>
        Warning: Converting a lead is permanent and cannot be undone. If you did not select a contact/client record that
        matches this lead, a new contact/client record will be created.
        <br />
        {existingCompanyWarning}
        <br />
        {existingCompanyWarning && this.renderRedirectLinkTag(companyName)}
      </>
    );
    ErrorMessage.SetWarningWithComment(message, 'Reason for converting', (reason) => {
      this.clickOnConvertButtonInPopup(id, { message: reason });
    });
  }

  clickOnConvertButtonInPopup(id, messageItem) {
    LeadService.ConvertLead(id, messageItem)
      .then((result) => {
        if (result.error) {
          ErrorMessage.SetWarning(result.error);
        } else {
          ErrorMessage.SetSuccess('The conversion has been finished successfully.');
        }
        this.refresh();
      })
      .catch((error) => {
        ErrorMessage.SetErrorMessage('LA156');
      });
  }

  refresh() {
    this.setState({ searchItem: { ...this.state.searchItem } });
  }

  handleExportExcelButtonClick() {
    LeadService.ExportAdminExcelAsync(this.state.searchItem).catch(() => ErrorMessage.SetErrorMessage('LA139'));
    ErrorMessage.SetSuccessWithWait(
      'File creation has started and your report will be available in your personal download menu soon. You can continue to use the system while your request is being processed.'
    );
  }

  handleButtonRowButtonClick(panelId) {
    panelId = panelId === this.state.shownPanel ? ShownPanelsEnum.None : panelId;
    this.setState({ shownPanel: panelId });
  }

  renderOnePanel(panelId, content) {
    return this.state.shownPanel === panelId ? content : null;
  }

  renderPanels() {
    return (
      <div>
        {this.renderOnePanel(
          ShownPanelsEnum.AddNewItem,
          <GeneralEditWizzardBase
            crudType={CrudTypesEnum.Create}
            editedItem={new LeadItem()}
            clickOnCancel={this.closeOverWindowWithoutRefresh.bind(this)}
            clickOnSave={this.closeOverWindowWithRefresh.bind(this)}
            windowDescription={EditLeadWindowDescription}
            errorCode={'LA141'}
          />
        )}
        {this.renderOnePanel(
          ShownPanelsEnum.Edit,
          <GeneralEditWizzardBase
            crudType={CrudTypesEnum.Update}
            editedItem={this.state.selectedItem}
            clickOnCancel={this.closeOverWindowWithoutRefresh.bind(this)}
            clickOnSave={this.closeOverWindowWithRefresh.bind(this)}
            windowDescription={EditLeadWindowDescription}
            errorCode={'LA152'}
          />
        )}
        {this.renderOnePanel(ShownPanelsEnum.EditItem, <div className='bg-white border rounded'>Edit Lead</div>)}
        {this.renderOnePanel(
          ShownPanelsEnum.MassUpload,
          <GeneralEditWizzardBase
            crudType={CrudTypesEnum.Create}
            editedItem={new LeadImportParameters()}
            clickOnCancel={this.closeOverWindowWithoutRefresh.bind(this)}
            clickOnSave={(edited, response) => {
              // A corrupted upload (leadImportId === 0) is mapped to a wizard error by
              // ImportLeadsWindowDescription.serviceFunction, so clickOnSave only runs for a
              // genuinely successful import (response.leadImportId > 0).
              const tempSearchItem = new LeadSearchParams();
              tempSearchItem.LeadImportId = response.leadImportId;
              // Setting searchItem (new object) drives the TabOfDataTable re-fetch; do NOT also
              // call this.refresh() — under React 18 batching it would re-spread the pre-update
              // searchItem and drop the LeadImportId filter we just set.
              this.setState({
                searchItem: tempSearchItem,
                shownPanel: ShownPanelsEnum.None,
                selected: -1,
                selectedUser: null,
              });
            }}
            windowDescription={ImportLeadsWindowDescription}
            errorCode='LA-IMPORT'
          />
        )}
      </div>
    );
  }

  renderButtonsAndPanels() {
    return (
      <>
        <ButtonRow
          colors={[ButtonColorsEnum.Primary, ButtonColorsEnum.Common]}
          tooltips={['Create', 'Import']}
          keys={[ShownPanelsEnum.AddNewItem, ShownPanelsEnum.MassUpload]}
          icons={['fa-solid fa-plus-circle', 'fa-solid fa-upload']}
          roleKeys={['lead_newlead_button', 'sales_leads']}
          value={this.state.shownPanel}
          onClick={this.handleButtonRowButtonClick}
        />
        {this.renderPanels()}
      </>
    );
  }

  closeOverWindowWithRefresh() {
    this.closeOverWindowWithoutRefresh();
    this.refresh();
  }

  closeOverWindowWithoutRefresh() {
    this.setState({
      shownPanel: ShownPanelsEnum.None,
    });
  }

  render() {
    return (
      <div className='contentwrapper'>
        {this.renderButtonsAndPanels()}
        <TabOfDataTable
          type={LeadItem}
          shownPanel={this.state.shownPanel}
          serviceFunction={LeadService.GetLeadListBySearching}
          convertDBItemsFunction={LeadService.ConvertDBItems}
          searchItem={this.state.searchItem}
          searchParamsType={LeadSearchParams}
          orderDirection={this.state.orderDirection}
          showSearchStrip={true}
          clickOnStartSearch={(searchItem) => this.setState({ searchItem })}
          singleSearchLine={true}
          isExportExcelDisabled={false}
          clickOnExportExcel={this.handleExportExcelButtonClick}
          excelExportRoleKey='reports_leadexport'
          singleSearchLinePlaceholder='Search in id, company name and email...'
          errorCode='LA224'
        />
      </div>
    );
  }
}
